import React, { useState, useEffect, useRef } from "react";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  ShoppingCart,
  Search,
  Plus,
  Trash2,
  Printer,
  DollarSign,
  Package,
  Calendar,
  ChevronDown,
  ChevronUp,
  Save,
  AlertCircle,
  RefreshCw,
  X,
} from "lucide-react";
import BatchChangeModal from "./BatchChangeModal";

const Cashier = () => {
  const receiptRef = useRef(null);
  const searchRef = useRef();
  const quantityInputRef = useRef();
  const { requestWithAuth } = useAuth();
  const { activeSession, company } = useGlobalContext();

  const [drugsDropdown, setDrugsDropdown] = useState(true);
  const [drugs, setDrugs] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [insertedDrugs, setInsertedDrugs] = useState([]);
  const [recordingSale, setRecordingSale] = useState(false);
  const [totalCost, setTotalCost] = useState(0.0);
  const [quantityRequested, setQuantityRequested] = useState("");
  const [transactionType, setTransactionType] = useState("Cash");
  const [recordedSale, setRecordedSale] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchModalIndex, setBatchModalIndex] = useState(null);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const listContainerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Helper function to get earliest expiring batch with stock
  const getEarliestBatch = (batches) => {
    const availableBatches = batches.filter(
      (b) => parseFloat(b.quantity) > 0 && !isBatchExpired(b.expiry_date)
    );
    if (availableBatches.length === 0) return null;

    return availableBatches.reduce((earliest, current) => {
      return new Date(current.expiry_date) < new Date(earliest.expiry_date)
        ? current
        : earliest;
    });
  };

  const isBatchExpired = (expiryDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  };

  // Helper function to check if expiry is soon (within 3 months)
  const isExpiringSoon = (expiryDate) => {
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return new Date(expiryDate) <= threeMonthsFromNow;
  };

  const checkExpiredBatches = (batches) => {
    return batches.filter(
      (b) => parseFloat(b.quantity) > 0 && isBatchExpired(b.expiry_date)
    );
  };

  const selectDrug = (drug) => {
    const totalStock = drug.batches.reduce(
      (sum, batch) => sum + parseFloat(batch.quantity),
      0
    );

    if (totalStock <= 0) {
      Swal.fire("", "This drug is out of stock", "error");
      return false;
    }

    // Auto-select earliest expiring batch
    const earliestBatch = getEarliestBatch(drug.batches);

    setSelectedDrug(drug);
    setSelectedBatch(earliestBatch);
    setDrugsDropdown(false);
    setSearch("");
    setDrugs([]);
  };

  const getDrugs = async (search_query) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/with-batches/${activeSession?.branch?.id}?search=${search_query}`,
        "GET"
      );
      setDrugs(data?.results);
    } catch (error) {
      toast.error("Something went wrong! Could not fetch drugs");
    } finally {
      setLoading(false);
      setSelectedResultIndex(-1);
    }
  };

  const recordSales = async () => {


    if (insertedDrugs.length <= 0) {
      return toast.error("Insert at least one drug");
    }

    Swal.fire({
      icon: "question",
      title: "Are you sure you want to record the sale?",
      text: "This action cannot be undone",
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setRecordingSale(true);

          const body = JSON.stringify({
            branch: activeSession?.branch?.id,
            session: activeSession?.session,
            transaction_type: transactionType,
            sale_items: insertedDrugs,
            amount_paid: totalCost,
          });

          const data = await requestWithAuth(`/inventory/sale/`, "POST", body, {
            "Content-Type": "application/json",
          });
          const saleData = data.payload;
          setRecordedSale(saleData);
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Sale Recorded Successfully",
          }).then(() => {
            setInsertedDrugs([]);
            setTotalCost(0.0);
            setSelectedDrug(null);
            setSelectedBatch(null);
            handlePrint(saleData);
          });
        } catch (error) {
          toast.error("Something went wrong!");
          setRecordingSale(false);
        } finally {
          setRecordingSale(false);
        }
      }
    });
  };

  const insertDrug = () => {
    if (!selectedDrug?.drug) {
      return toast.error("Select a drug first");
    }
    if (!selectedBatch) {
      return toast.error("No batch available");
    }
    if (!quantityRequested || quantityRequested <= 0) {
      return toast.error("Please enter a valid quantity");
    }

    const drugId = selectedDrug?.drug?.id;
    let remainingQty = parseFloat(quantityRequested);

    // Get total available stock across all batches
    const totalAvailableStock = selectedDrug.batches.reduce(
      (sum, batch) => sum + parseFloat(batch.quantity),
      0
    );

    if (remainingQty > totalAvailableStock) {
      return toast.error(
        `Only ${totalAvailableStock} units available across all batches`
      );
    }

    // Get available batches sorted by expiry date (earliest first)
    const availableBatches = selectedDrug.batches
      .filter((b) => parseFloat(b.quantity) > 0)
      .sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date));

    const newInsertedDrugs = [...insertedDrugs];
    let batchesUsed = [];

    // Distribute quantity across batches starting with earliest expiry
    for (const batch of availableBatches) {
      if (remainingQty <= 0) break;

      // Check if this batch is already in cart
      const existingItemIndex = newInsertedDrugs.findIndex(
        (item) =>
          item.drug === drugId && item.batch_number === batch.batch_number
      );

      const batchAvailableQty = parseFloat(batch.quantity);
      const qtyToAdd = Math.min(remainingQty, batchAvailableQty);

      if (existingItemIndex >= 0) {
        // Update existing item in cart
        const currentQty = parseFloat(
          newInsertedDrugs[existingItemIndex].quantity
        );
        const newQty = currentQty + qtyToAdd;

        if (newQty > batchAvailableQty) {
          const canAdd = batchAvailableQty - currentQty;
          if (canAdd > 0) {
            newInsertedDrugs[existingItemIndex].quantity =
              batchAvailableQty.toString();
            const unitPrice = parseFloat(
              newInsertedDrugs[existingItemIndex].unit_price
            );
            newInsertedDrugs[existingItemIndex].total_cost = (
              unitPrice * batchAvailableQty
            ).toFixed(2);
            remainingQty -= canAdd;
            batchesUsed.push({ batch: batch.batch_number, qty: canAdd });
          }
        } else {
          newInsertedDrugs[existingItemIndex].quantity = newQty.toString();
          const unitPrice = parseFloat(
            newInsertedDrugs[existingItemIndex].unit_price
          );
          newInsertedDrugs[existingItemIndex].total_cost = (
            unitPrice * newQty
          ).toFixed(2);
          remainingQty -= qtyToAdd;
          batchesUsed.push({ batch: batch.batch_number, qty: qtyToAdd });
        }
      } else {
        // Add new item to cart
        const total_cost =
          parseFloat(selectedDrug?.drug?.selling_price) * qtyToAdd;
        newInsertedDrugs.push({
          drug: selectedDrug?.drug?.id,
          drug_name: selectedDrug?.drug?.name,
          batch_number: batch.batch_number,
          expiry_date: batch.expiry_date,
          available_quantity: batch.quantity,
          quantity: qtyToAdd.toString(),
          unit_price: selectedDrug?.drug?.selling_price,
          total_cost: total_cost.toFixed(2),
          has_multiple_batches:
            selectedDrug?.batches?.filter((b) => parseFloat(b.quantity) > 0)
              .length > 1,
        });
        remainingQty -= qtyToAdd;
        batchesUsed.push({ batch: batch.batch_number, qty: qtyToAdd });
      }
    }

    // Show notification about batch distribution
    if (batchesUsed.length > 1) {
      const batchInfo = batchesUsed
        .map((b) => `${b.qty} from ${b.batch}`)
        .join(", ");
      toast.success(`Split across ${batchesUsed.length} batches: ${batchInfo}`);
    }

    setInsertedDrugs(newInsertedDrugs);
    setSelectedDrug(null);
    setSelectedBatch(null);
    setQuantityRequested("");

    let total = 0;
    newInsertedDrugs.forEach((item) => {
      total += parseFloat(item?.total_cost);
    });
    setTotalCost(total.toFixed(2));
    setDrugsDropdown(true);
  };

  const removeInsertedDrug = (index) => {
    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs.splice(index, 1);
    setInsertedDrugs(newInsertedDrugs);

    let total = 0;
    newInsertedDrugs.forEach((item) => {
      total += parseFloat(item?.total_cost);
    });
    setTotalCost(total.toFixed(2));
  };

  const updateInsertedDrug = (index, new_quantity) => {
    const newInsertedDrugs = [...insertedDrugs];
    const availableQty =
      parseFloat(newInsertedDrugs[index]?.available_quantity) || 0;
    const unitPrice = parseFloat(newInsertedDrugs[index]?.unit_price) || 0;

    if (parseFloat(new_quantity) > availableQty) {
      return toast.error(
        "Quantity requested is greater than available stock in this batch"
      );
    }

    newInsertedDrugs[index].quantity = new_quantity;
    const parsedQty = parseFloat(new_quantity) || 0;
    newInsertedDrugs[index].total_cost = (unitPrice * parsedQty).toFixed(2);

    setInsertedDrugs(newInsertedDrugs);

    let total = 0;
    newInsertedDrugs.forEach((item) => {
      total += parseFloat(item?.total_cost) || 0;
    });

    setTotalCost(total.toFixed(2));
  };

  const openBatchModal = (index) => {
    setBatchModalIndex(index);
    setShowBatchModal(true);
  };

  const changeBatch = (newBatch) => {
    if (batchModalIndex !== null) {
      const newInsertedDrugs = [...insertedDrugs];
      const item = newInsertedDrugs[batchModalIndex];

      // Check if new batch already exists in cart
      const batchExists = insertedDrugs.some(
        (drug, idx) =>
          idx !== batchModalIndex &&
          drug.drug === item.drug &&
          drug.batch_number === newBatch.batch_number
      );

      if (batchExists) {
        toast.error("This batch is already in the cart");
        return;
      }

      // Update batch information
      newInsertedDrugs[batchModalIndex] = {
        ...item,
        batch_number: newBatch.batch_number,
        expiry_date: newBatch.expiry_date,
        available_quantity: newBatch.quantity,
        quantity: Math.min(
          parseFloat(item.quantity),
          parseFloat(newBatch.quantity)
        ).toString(),
      };

      // Recalculate cost
      const newQty = parseFloat(newInsertedDrugs[batchModalIndex].quantity);
      const unitPrice = parseFloat(
        newInsertedDrugs[batchModalIndex].unit_price
      );
      newInsertedDrugs[batchModalIndex].total_cost = (
        newQty * unitPrice
      ).toFixed(2);

      setInsertedDrugs(newInsertedDrugs);

      // Recalculate total
      let total = 0;
      newInsertedDrugs.forEach((item) => {
        total += parseFloat(item?.total_cost) || 0;
      });
      setTotalCost(total.toFixed(2));

      setShowBatchModal(false);
      setBatchModalIndex(null);
    }
  };

  const handlePrint = (saleData) => {
    if (!saleData) return;

    // Consolidate items by drug (merge same drugs from different batches)
    const consolidatedItems =
      saleData?.items?.reduce((acc, item) => {
        const existingItem = acc.find((i) => i.drug === item.drug);
        if (existingItem) {
          // Add quantities and costs together
          existingItem.quantity = (
            parseFloat(existingItem.quantity) + parseFloat(item.quantity)
          ).toFixed(2);
          existingItem.total_selling_cost = (
            parseFloat(existingItem.total_selling_cost) +
            parseFloat(item.total_selling_cost)
          ).toFixed(2);
        } else {
          // Add new item
          acc.push({ ...item });
        }
        return acc;
      }, []) || [];

    const printWindow = window.open("", "_blank", "width=800,height=900");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Receipt</title>
          <style>
            @media print {
              @page {
                size: 58mm auto;
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              width: 58mm;
              padding: 5mm;
              margin: 0 auto;
            }
            
            .receipt-header {
              text-align: center;
              margin-bottom: 10px;
              padding-bottom: 10px;
              border-bottom: 2px dashed #000;
            }
            
            .company-name {
              font-size: 16px;
              font-weight: bold;
              text-transform: uppercase;
              margin-bottom: 3px;
            }
            
            .branch-name {
              font-size: 13px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .receipt-info {
              font-size: 11px;
              margin-top: 5px;
            }
            
            .receipt-info div {
              display: flex;
              justify-content: space-between;
              margin-bottom: 2px;
            }
            
            .divider {
              border-top: 1px dashed #000;
              margin: 8px 0;
            }
            
            .divider-thick {
              border-top: 2px solid #000;
              margin: 10px 0;
            }
            
            .items-section {
              margin: 10px 0;
            }
            
            .item {
              margin-bottom: 8px;
              font-size: 11px;
            }
            
            .item-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              margin-bottom: 2px;
            }
            
            .item-details {
              display: flex;
              justify-content: space-between;
              color: #333;
              padding-left: 10px;
              font-size: 10px;
            }
            
            .totals {
              margin-top: 10px;
              padding-top: 10px;
              border-top: 2px dashed #000;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .total-row.grand-total {
              font-size: 14px;
              font-weight: bold;
              margin-top: 5px;
              padding-top: 5px;
              border-top: 2px solid #000;
            }
            
            .payment-info {
              margin-top: 10px;
              padding-top: 10px;
              border-top: 1px dashed #000;
              font-size: 11px;
            }
            
            .payment-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 3px;
            }
            
            .footer {
              text-align: center;
              margin-top: 15px;
              padding-top: 10px;
              border-top: 2px dashed #000;
              font-size: 10px;
            }
            
            .footer-message {
              margin-bottom: 5px;
            }
            
            .footer-note {
              font-size: 9px;
              color: #666;
              font-style: italic;
            }
            
            .print-time {
              margin-top: 8px;
              font-size: 9px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="receipt-header">
            <div class="company-name">Life Chemist Ltd.</div>
            <div class="branch-name">${saleData?.branch || "Main Branch"} (${activeSession?.session || ""})</div>
            <p>Phone Number: ${activeSession?.branch?.phone_number || "N/A"}</p>
            <div class="receipt-info">
              <div>
                <span>Invoice:</span>
                <span><strong>${saleData?.items?.[0]?.sale || "N/A"
      }</strong></span>
              </div>
              <div>
                <span>Date:</span>
                <span>${new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}</span>
              </div>
              <div>
                <span>Time:</span>
                <span>${new Date().toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })}</span>
              </div>
            </div>
          </div>
          
          <div class="items-section">
            ${consolidatedItems
        .map(
          (item, index) => `
              <div class="item">
                <div class="item-header">
                  <span>${index + 1}. ${item.drug}</span>
                </div>
                <div class="item-details">
                  <span>${parseFloat(item.quantity).toFixed(
            0
          )} x GHS ${parseFloat(item.unit_cost).toFixed(2)}</span>
                  <span><strong>GHS ${parseFloat(
            item.quantity * item.unit_cost
          ).toFixed(2)}</strong></span>
                </div>
              </div>
            `
        )
        .join("")}
          </div>
          
          <div class="totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>GHS ${parseFloat(saleData?.total_amount || 0).toFixed(
          2
        )}</span>
            </div>
            <div class="total-row grand-total">
              <span>TOTAL:</span>
              <span>GHS ${parseFloat(saleData?.total_amount || 0).toFixed(
          2
        )}</span>
            </div>
          </div>
          
          <div class="payment-info">
            <div class="payment-row">
              <span>Payment Method:</span>
              <span><strong>${transactionType}</strong></span>
            </div>
            ${transactionType === "Cash" && saleData?.cash_collected
        ? `
              <div class="payment-row">
                <span>Cash Received:</span>
                <span>GHS ${parseFloat(saleData?.cash_collected || 0).toFixed(
          2
        )}</span>
              </div>
              <div class="payment-row">
                <span>Change:</span>
                <span>GHS ${parseFloat(saleData?.cash_change || 0).toFixed(
          2
        )}</span>
              </div>
            `
        : ""
      }
          </div>
          
          <div class="footer">
            <div class="footer-message">
              <strong>Thank you for your purchase!</strong>
            </div>
            <p>Head Office: ${company?.phone_number || "N/A"}</p>
            <div class="footer-note">
              Please keep this receipt for your records
            </div>
            <div class="footer-message">
              <strong>NB: ITEMS SOLD ARE NOT RETURNABLE</strong>
            </div>
            <div class="print-time">
              Printed: ${new Date().toLocaleString("en-GB")}
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Auto-print after content loads
    printWindow.onload = function () {
      printWindow.focus();
      printWindow.print();
    };
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (debouncedSearch.length > 2) {
      getDrugs(debouncedSearch);
    } else {
      setDrugs([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (drugsDropdown) {
      // Small timeout to ensure element is rendered
      setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
    } else {
      setSelectedResultIndex(-1);
    }
  }, [drugsDropdown]);

  const handleSearchKeyDown = (e) => {
    if (!drugs.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedResultIndex((prev) =>
        prev < drugs.length - 1 ? prev + 1 : prev
      );
      // Optional: scroll into view logic could go here
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedResultIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedResultIndex >= 0 && selectedResultIndex < drugs.length) {
        selectDrug(drugs[selectedResultIndex]);
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && !drugsDropdown && !showBatchModal) {
        insertDrug();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedDrug,
    selectedBatch,
    quantityRequested,
    drugsDropdown,
    showBatchModal,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        recordSales();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [insertedDrugs, totalCost]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDrugsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedResultIndex >= 0 && listContainerRef.current) {
      const selectedElement = listContainerRef.current.querySelector(
        `#drug-result-${selectedResultIndex}`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedResultIndex]);

  // Auto-focus quantity input when drug is selected
  useEffect(() => {
    if (selectedDrug && selectedBatch && quantityInputRef.current) {
      setTimeout(() => {
        quantityInputRef.current?.focus();
      }, 100);
    }
  }, [selectedDrug, selectedBatch]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Hidden Receipt */}
      <div className="hidden">
        <div ref={receiptRef} className="receipt">
          <h2>Life Chemist Ltd. ({recordedSale?.branch})</h2>
          <p>Invoice #: {recordedSale?.items?.[0]?.sale}</p>
          <p>Date: {new Date().toLocaleDateString()}</p>
          <hr />
          <table>
            <tbody>
              {recordedSale?.items?.map((item, index) => (
                <tr key={index}>
                  <td>{item.drug}</td>
                  <td>
                    {item.quantity} x GHS{item.unit_cost}
                  </td>
                  <td>GHS{item.total_selling_cost}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <hr />
          <p>
            <strong>Total: GHS {recordedSale?.total_amount}</strong>
          </p>
          <p>Thank you for your purchase!</p>
        </div>
      </div>

      {/* Batch Change Modal */}
      {showBatchModal &&
        batchModalIndex !== null &&
        (() => {
          const currentItem = insertedDrugs[batchModalIndex];

          // Find the drug data from the originally fetched drugs
          // You may need to fetch this data again if it's not available
          return (
            <BatchChangeModal
              show={showBatchModal}
              onClose={() => {
                setShowBatchModal(false);
                setBatchModalIndex(null);
              }}
              currentItem={currentItem}
              onSelectBatch={changeBatch}
              branch={activeSession?.branch?.id}
            />
          );
        })()}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Sales</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Cashier</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <ShoppingCart size={32} />
                Point of Sale
              </h1>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm text-gray-600">Branch</p>
              <p className="text-lg font-semibold text-gray-900">
                {activeSession?.branch?.name}
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Sale Entry */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Selection Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Package className="text-blue-500" size={24} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Product Selection
                    </h2>
                    <p className="text-sm text-gray-500">
                      Search and add products to cart
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Transaction Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction Type
                  </label>
                  <select
                    onChange={(e) => setTransactionType(e.target.value)}
                    value={transactionType}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Mobile Money">Mobile Money</option>
                  </select>
                </div>

                {/* Drug Search */}
                <div className="relative" ref={dropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Product
                  </label>
                  <div
                    className="relative cursor-pointer border border-gray-300 rounded-lg px-4 py-2.5 text-sm flex justify-between items-center"
                    onClick={() => {
                      setDrugsDropdown(!drugsDropdown);
                    }}
                  >
                    <span
                      className={
                        selectedDrug?.drug?.name
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {selectedDrug?.drug?.name || "Select a product..."}
                    </span>
                    {drugsDropdown ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>

                  {drugsDropdown && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg"
                    >
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={18}
                          />
                          <input
                            ref={searchRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            type="text"
                            placeholder="Type to search..."
                            onKeyDown={handleSearchKeyDown}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto" ref={listContainerRef}>
                        {debouncedSearch?.length < 3 ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Please enter at least 3 characters
                          </p>
                        ) : loading ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Searching...
                          </p>
                        ) : drugs?.length > 0 ? (
                          drugs.map((drug_item, idx) => {
                            const totalStock = drug_item.batches.reduce(
                              (sum, batch) => sum + parseFloat(batch.quantity),
                              0
                            );
                            return (
                              <div
                                key={idx}
                                id={`drug-result-${idx}`}
                                onClick={() => selectDrug(drug_item)}
                                className={`p-3 cursor-pointer border-b border-gray-100 last:border-0 ${idx === selectedResultIndex
                                  ? "bg-blue-300"
                                  : "hover:bg-blue-50"
                                  }`}
                              >
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-900">
                                    {drug_item?.drug?.name}
                                  </span>
                                  <span className="text-sm font-semibold text-blue-600">
                                    GHS {drug_item?.drug?.selling_price}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Total Stock: {totalStock} |{" "}
                                  {drug_item.batches.length} batch(es)
                                </p>
                              </div>
                            );
                          })
                        ) : (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            No results found
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product & Batch Details Grid */}
                {selectedDrug?.drug && selectedBatch && (
                  <div className="space-y-3">
                    {/* Batch Warning if expiring soon */}
                    {isExpiringSoon(selectedBatch.expiry_date) && (
                      <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <AlertCircle
                          className="text-orange-500 flex-shrink-0 mt-0.5"
                          size={18}
                        />
                        <div className="text-sm">
                          <p className="font-medium text-orange-900">
                            Expiring Soon
                          </p>
                          <p className="text-orange-700">
                            This batch expires on{" "}
                            {new Date(
                              selectedBatch.expiry_date
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Primary Batch
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          {selectedBatch?.batch_number}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Available Stock
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          {selectedBatch?.quantity} units
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Unit Price
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          GHS {selectedDrug?.drug?.selling_price}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          <Calendar size={12} className="inline mr-1" />
                          Expiry Date
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          {new Date(
                            selectedBatch?.expiry_date
                          ).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantity
                        </label>
                        <input
                          ref={quantityInputRef}
                          type="number"
                          min="0"
                          step="1"
                          value={quantityRequested}
                          onChange={(e) => setQuantityRequested(e.target.value)}
                          placeholder="0"
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Batch Distribution Preview */}
                      {quantityRequested &&
                        parseFloat(quantityRequested) > 0 && (
                          <div className="col-span-2">
                            {(() => {
                              const requestedQty =
                                parseFloat(quantityRequested);
                              const availableBatches = selectedDrug.batches
                                .filter((b) => parseFloat(b.quantity) > 0)
                                .sort(
                                  (a, b) =>
                                    new Date(a.expiry_date) -
                                    new Date(b.expiry_date)
                                );

                              const totalAvailable = availableBatches.reduce(
                                (sum, b) => sum + parseFloat(b.quantity),
                                0
                              );

                              if (requestedQty > totalAvailable) {
                                return (
                                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <AlertCircle
                                        className="text-red-500 flex-shrink-0 mt-0.5"
                                        size={16}
                                      />
                                      <div className="text-xs text-red-700">
                                        <p className="font-medium">
                                          Insufficient Stock
                                        </p>
                                        <p>
                                          Only {totalAvailable} units available
                                          across all batches
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              // Calculate distribution
                              let remaining = requestedQty;
                              const distribution = [];

                              for (const batch of availableBatches) {
                                if (remaining <= 0) break;
                                const batchQty = parseFloat(batch.quantity);
                                const qtyFromBatch = Math.min(
                                  remaining,
                                  batchQty
                                );
                                distribution.push({
                                  batch: batch.batch_number,
                                  qty: qtyFromBatch,
                                  expiry: batch.expiry_date,
                                });
                                remaining -= qtyFromBatch;
                              }

                              if (distribution.length > 1) {
                                return (
                                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <Package
                                        className="text-blue-500 flex-shrink-0 mt-0.5"
                                        size={16}
                                      />
                                      <div className="text-xs">
                                        <p className="font-medium text-blue-900 mb-1">
                                          Will use {distribution.length}{" "}
                                          batches:
                                        </p>
                                        {distribution.map((d, idx) => (
                                          <div
                                            key={idx}
                                            className="text-blue-700"
                                          >
                                            • {d.qty} from {d.batch} (exp:{" "}
                                            {new Date(
                                              d.expiry
                                            ).toLocaleDateString()}
                                            )
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              return null;
                            })()}
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={insertDrug}
                  disabled={
                    !selectedDrug?.drug || !quantityRequested || !selectedBatch
                  }
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Add to Cart
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Cart */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ShoppingCart size={20} />
                  Shopping Cart ({insertedDrugs.length})
                </h2>
              </div>

              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {insertedDrugs.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {insertedDrugs.map((drug, index) => (
                      <div key={index} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              {drug?.drug_name}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                Batch: {drug?.batch_number}
                              </span>
                              {isExpiringSoon(drug?.expiry_date) && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                                  Expiring Soon
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Exp:{" "}
                              {new Date(drug?.expiry_date).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => removeInsertedDrug(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="flex items-center justify-between gap-2 mt-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              value={drug?.quantity}
                              onChange={(e) =>
                                updateInsertedDrug(index, e.target.value)
                              }
                              className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center"
                            />
                            <span className="text-xs text-gray-500">
                              × GHS {drug?.unit_price}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            GHS {drug?.total_cost}
                          </span>
                        </div>

                        {drug?.has_multiple_batches && (
                          <button
                            onClick={() => openBatchModal(index)}
                            className="mt-3 w-full text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1 py-1.5 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                          >
                            <RefreshCw size={12} />
                            Change Batch
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <ShoppingCart
                      size={48}
                      className="mx-auto text-gray-300 mb-3"
                    />
                    <p className="text-sm text-gray-500">Your cart is empty</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add products to get started
                    </p>
                  </div>
                )}
              </div>

              {insertedDrugs.length > 0 && (
                <div className="border-t border-gray-200">
                  <div className="p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-gray-900">
                        Total
                      </span>
                      <span className="text-2xl font-bold text-blue-600">
                        GHS {totalCost}
                      </span>
                    </div>

                    {/* Record Sale Button */}
                    <button
                      onClick={recordSales}
                      disabled={recordingSale}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                    >
                      {recordingSale ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Recording Sale...
                        </>
                      ) : (
                        <>
                          <Save size={20} />
                          Record Sale (Alt+S)
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cashier;
